import React from "react";
import { Box, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import HPDropdown from "@/components/common/Dropdown";
import RollupBuilder from "@/components/modals/chart-modal/RollupBuilder";
import type { QuestionValue, Variable } from "@/models/Dataset";

export type GridMode = "value" | "rollup";

interface GridValueOrRollupSelectorProps {
  gridMode: GridMode;
  onGridModeChange: (mode: GridMode) => void;
  values: QuestionValue[];
  selectedGridValueId: string;
  onGridValueChange: (valueId: string) => void;
  rollupName: string;
  rollupSelectedLabels: string[];
  onRollupNameChange: (name: string) => void;
  onRollupLabelsChange: (labels: string[]) => void;
  isDisabled?: boolean;
  /** "value" (default) picks/rolls up response values — used for Single/Single
   *  grid questions. "variable" picks/rolls up variables instead — used for
   *  Multiple questions, where each response option is its own variable. */
  focusMode?: "value" | "variable";
  /** Required when focusMode="variable" — the question's variables to choose
   *  a single one from, or roll up. */
  variables?: Variable[];
}

const GridValueOrRollupSelector: React.FC<GridValueOrRollupSelectorProps> = ({
  gridMode,
  onGridModeChange,
  values,
  selectedGridValueId,
  onGridValueChange,
  rollupName,
  rollupSelectedLabels,
  onRollupNameChange,
  onRollupLabelsChange,
  isDisabled,
  focusMode = "value",
  variables = [],
}) => {
  const isVariableMode = focusMode === "variable";
  const gridValueOptions = isVariableMode
    ? variables.map((v) => ({ value: v.id, label: v.rowtitle || v.title }))
    : values.map((v) => ({
        value: v.id,
        label: v.title || v.label,
      }));

  return (
    <Box>
      <FormLabel sx={{ fontSize: 12, color: "#262626", mb: 0.5, display: "block" }}>
        Response Selection
      </FormLabel>
      <RadioGroup
        value={gridMode}
        onChange={(e) => onGridModeChange(e.target.value as GridMode)}
      >
        <FormControlLabel
          value="value"
          control={<Radio size="small" />}
          label="Single Category"
          disabled={isDisabled}
        />
        <FormControlLabel
          value="rollup"
          control={<Radio size="small" />}
          label="Rollup"
          disabled={isDisabled}
        />
      </RadioGroup>

      {gridMode === "value" ? (
        <Box sx={{ mt: 1.5 }}>
          <HPDropdown
            label={isVariableMode ? "Response Value*" : "Grid Value*"}
            value={selectedGridValueId}
            onChange={onGridValueChange}
            placeholder={isVariableMode ? "Choose a response value" : "Choose a grid value"}
            options={gridValueOptions}
            disabled={isDisabled}
          />
        </Box>
      ) : (
        <Box sx={{ mt: 1.5 }}>
          <RollupBuilder
            focusMode={focusMode}
            allVariables={isVariableMode ? variables : []}
            allValues={isVariableMode ? [] : values}
            rollupName={rollupName}
            rollupSelectedLabels={rollupSelectedLabels}
            onRollupNameChange={onRollupNameChange}
            onSelectedLabelsChange={onRollupLabelsChange}
          />
        </Box>
      )}
    </Box>
  );
};

export default GridValueOrRollupSelector;
